import { type FC } from 'react';
import { Provider } from 'react-redux';
import { initializeDBL } from 'dibs-buyer-layout/src/entries/index.jsx';
import { IntlProvider } from 'dibs-react-intl';
import { useLazyLoadQuery, RelayEnvironmentProvider } from 'react-relay';

import { HpSharedLayout } from '../homepage/HpSharedLayout.tsx';
import { homepageRootQuery } from '../queries/homepageRoot.ts';
import serverVars from 'server-vars';
import { hydrateRoot } from 'react-dom/client';
import { HomePageContextProvider } from '../homepage/HomePageContext.tsx';
import { getRelayEnvironment } from '../utils/relayModernEnvironment.js';
import { Error500Layout } from 'dibs-buyer-layout/exports/error500.tsx';
import { ErrorBoundary } from 'dibs-error-tools/ErrorBoundary.tsx';
import { getIsBotOnClient } from 'dibs-buyer-layout/exports/botHelpers.ts';
import { HomepagePersonalizedCollectionsProvider } from 'dibs-personalized/exports/PersonalizedCollectionsContext.ts';

import { type homepageRootQuery as homepageRootQueryType } from '../queries/__generated__/homepageRootQuery.graphql.ts';

const ComponentWrapper: FC = () => {
    const data = useLazyLoadQuery<homepageRootQueryType>(
        homepageRootQuery,
        serverVars.get('relayVariables'),
        {
            fetchPolicy: 'store-only',
        }
    );
    return <HpSharedLayout viewer={data.viewer} />;
};

initializeDBL().then(({ store }) => {
    const environment = getRelayEnvironment(serverVars.get('dbl.relayData'));

    const jsRoot = document.getElementById('js-root');
    if (!jsRoot) {
        return;
    }

    hydrateRoot(
        jsRoot,
        <IntlProvider locale={serverVars.get('locale')} messages={serverVars.get('messages')}>
            <ErrorBoundary fallback={({ error }) => <Error500Layout error={error} />}>
                <RelayEnvironmentProvider environment={environment}>
                    <HomePageContextProvider
                        isBot={getIsBotOnClient()}
                        isMobile={serverVars.get('settings.isMobile')}
                        isTablet={serverVars.get('settings.isTablet')}
                        isDesktop={serverVars.get('settings.isDesktop')}
                        userType={serverVars.get('relayVariables.userType')}
                        isLoggedInUser={serverVars.get('isLoggedInUser')}
                        priceBookName={serverVars.get('relayVariables.priceBookName')}
                        isTrade={!!serverVars.get('relayVariables.isTrade')}
                        isTopDisplayAdEnabled={serverVars.get('isTopDisplayAdEnabled')}
                        isBottomDisplayAdEnabled={serverVars.get('isBottomDisplayAdEnabled')}
                        isRemoveTopHeroBannerTestVariant={serverVars.get(
                            'isRemoveTopHeroBannerTestVariant'
                        )}
                        isEvergreenCollectionsTestVariant={serverVars.get(
                            'isEvergreenCollectionsTestVariant'
                        )}
                    >
                        <HomepagePersonalizedCollectionsProvider>
                            <Provider store={store}>
                                <ComponentWrapper />
                            </Provider>
                        </HomepagePersonalizedCollectionsProvider>
                    </HomePageContextProvider>
                </RelayEnvironmentProvider>
            </ErrorBoundary>
        </IntlProvider>
    );
});
